import { FormControl, FormHelperText } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import React, { memo } from 'react';
import { Controller } from 'react-hook-form';

/**
 * ControlledRadioGroup is a wrapper around the Controller component from react-hook-form.
 * It is used to control a group of radio buttons in the form.
 * @param {object} props
 * @param {string} props.name - The name of the input field
 * @param {import('react-hook-form').Control<any, any>} props.control - The control object from react-hook-form
 * @param {string} [props.label] - The label for the radio group
 * @param {boolean} [props.required] - The required state of the input field
 * @param {boolean} [props.error] - The error state of the input field
 * @param {string | React.ReactNode | any} [props.helperText] - The helper text for the input field
 * @param {Array<{ value: string, label: React.ReactNode, disabled?: boolean }>} [props.options] - The radio options to render
 * @param {Omit<import('react-hook-form').UseControllerProps, 'name'>} [props.controllerOptions] - The options for the controller
 * @param {import('@mui/material/RadioGroup').RadioGroupProps} [props.radioGroupProps] - The props for the radio group
 * @param {import('@mui/material/FormLabel').FormLabelProps} [props.formLabelProps] - The props for the form label
 */

const ControlledRadioGroupComponent = ({
  name,
  control,
  label = '',
  required = false,
  error = false,
  helperText = '',
  options = [],
  controllerOptions = {},
  radioGroupProps = {},
  formLabelProps = {},
}) => {
  if (!name) {
    throw new Error('ControlledRadioGroup: name is required');
  }
  if (!(controllerOptions?.control || control)) {
    throw new Error('ControlledRadioGroup: control is required');
  }

  const id = `id_${name}`;

  return (
    <Controller
      control={control}
      name={name}
      {...controllerOptions}
      render={({ field, fieldState, formState }) => (
        <FormControl error={error} required={required}>
          {label ? (
            <FormLabel id={`labelId_${id}`} {...formLabelProps}>
              {label}
            </FormLabel>
          ) : null}
          <RadioGroup
            aria-labelledby={`labelId_${id}`}
            id={id}
            {...field}
            {...radioGroupProps}
          >
            {options.map((option) => (
              <FormControlLabel
                control={<Radio size="small" />}
                disabled={option.disabled}
                key={option.value}
                label={option.label}
                value={option.value}
              />
            ))}
          </RadioGroup>
          {error ? <FormHelperText error>{helperText}</FormHelperText> : null}
        </FormControl>
      )}
    />
  );
};

ControlledRadioGroupComponent.displayName = 'ControlledRadioGroup';

const ControlledRadioGroup = memo(ControlledRadioGroupComponent);

export default ControlledRadioGroup;
